import { customFieldRegExp } from "hooks/use-available-custom-fields";
import { MessageButton } from "../conversations/message-domain";
import WhatsappTemplate from "../whatsapp-template";
import FullTemplateDomain, { TemplateCategory } from "./full_template";

export type CustomFields = {
  [key in CustomFieldKey]?: string;
};

export type CustomFieldKey =
  | "contact_first_name"
  | "contact_last_name"
  | "email_address"
  | "phone_number"
  | "agent_first_name"
  | "agent_last_name"
  | "address"
  | "review_link"
  | "merchant_name";

class TemplateDomain {
  public id: string;

  public name: string;

  public title: string;

  public text: string;

  public purpose: string | null;

  public customFields: CustomFields;

  public favourite: boolean;

  public shortcut: string | null;

  public channels: string[];

  public subject: string | null;

  public isEditable: boolean;

  public category: TemplateCategory | null;

  public mediaUrl: string | null;

  public mediaType: string | null;

  public buttons?: MessageButton[];

  /** @deprecated Only the first folder; use folderIds. */
  public folderId?: string | null;

  // Optional so FullTemplateDomain (regular templates, which have no folders)
  // stays structurally assignable; absent means unfiled.
  public folderIds?: string[];

  constructor(
    id: string,
    name: string,
    title: string,
    text: string,
    purpose: string | null,
    customFields: CustomFields,
    favourite: boolean,
    shortcut: string | null,
    channels: string[],
    subject: string | null,
    isEditable: boolean,
    category: TemplateCategory | null,
    mediaUrl: string | null,
    mediaType: string | null,
    buttons: MessageButton[] = [],
    folderId: string | null = null,
    folderIds: string[] = []
  ) {
    this.id = id;
    this.name = name;
    this.title = title;
    this.text = text;
    this.purpose = purpose;
    this.customFields = customFields;
    this.favourite = favourite;
    this.shortcut = shortcut;
    this.channels = channels;
    this.subject = subject;
    this.isEditable = isEditable;
    this.category = category;
    this.mediaUrl = mediaUrl;
    this.mediaType = mediaType;
    this.buttons = buttons;
    this.folderId = folderId;
    this.folderIds = folderIds;
  }

  getWhatsAppStatusColor(): string {
    return "";
  }

  public static containsCustomFieldsWithoutValue(
    message: string,
    customFields: CustomFields
  ): boolean {
    let hasIssues = false;

    TemplateDomain.forEachCustomField(
      message,
      customFields,
      (_startsAt, _endsAt, key: CustomFieldKey) => {
        if (!customFields[key]) {
          hasIssues = true;
        }
      }
    );

    return hasIssues;
  }

  public static getCustomFieldsWithoutValue(
    message: string,
    customFields: CustomFields
  ): string[] {
    const customFieldsWithoutValues: string[] = [];

    TemplateDomain.forEachCustomField(
      message,
      customFields,
      (_startsAt, _endsAt, key: CustomFieldKey) => {
        if (!customFields[key]) {
          customFieldsWithoutValues.push(key);
        }
      }
    );

    return customFieldsWithoutValues;
  }

  public static forEachCustomField(
    message: string,
    customFields: CustomFields,
    callback: (
      startsAt: number,
      endsAt: number,
      key: CustomFieldKey
    ) => string | void
  ): void {
    const isMessageEmpty = !message.trim();
    const noCustomFields = Object.keys(customFields).length === 0;

    if (isMessageEmpty || noCustomFields) {
      return;
    }

    let textToIterate = message;
    let lastMatch;
    const findCustomField = new RegExp(customFieldRegExp, "g");

    while ((lastMatch = findCustomField.exec(textToIterate))) {
      const startsAt = lastMatch.index;
      let endsAt = startsAt + 1;
      const charactersFromText = textToIterate.split("");

      while (endsAt < charactersFromText.length) {
        const letter = charactersFromText[endsAt];

        if (/[\W]/.test(letter)) break;

        endsAt++;
      }

      const result = callback(startsAt, endsAt, lastMatch[1] as CustomFieldKey);

      if (typeof result === "string") {
        textToIterate = result;
      }

      if (!findCustomField.global) break; // To be safe for a moment
    }
  }

  public static getTextFromTemplate(
    message: string,
    customFields: CustomFields
  ): string {
    let textFromTemplate = message;

    Object.keys(customFields).forEach((key) => {
      const value = customFields[key as CustomFieldKey];

      if (value) {
        textFromTemplate = textFromTemplate.replaceAll(`{${key}}`, value);
      }
    });

    return textFromTemplate.trim();
  }

  getMedia(): {
    type: string;
    url: string;
  } | null {
    if (this.mediaUrl && this.mediaType) {
      return {
        type: this.mediaType,
        url: this.mediaUrl,
      };
    }

    return null;
  }

  getWholeText(): string {
    return this.text;
  }

  isWhatsappTemplate(): this is WhatsappTemplate {
    return false;
  }

  isRegularTemplate(): this is FullTemplateDomain {
    return true;
  }

  canEdit(): boolean {
    return false;
  }

  getCustomFieldKeys(): string[] {
    const customFieldKeys: string[] = [];

    TemplateDomain.forEachCustomField(
      this.getWholeText(),
      this.customFields,
      (_startsAt, _endsAt, key) => {
        if (!customFieldKeys.includes(key)) {
          customFieldKeys.push(key);
        }
      }
    );

    return customFieldKeys;
  }

  isGeneric(): boolean {
    return false;
  }

  isCustom(): boolean {
    return true;
  }
}

export default TemplateDomain;
